/**
 * Zusammengesetzte Antworten der API (Listen, Arbeitsstaende, Dashboard), die
 * API und SPA gemeinsam kennen. Die API typisiert ihre Rueckgaben mit diesen
 * Interfaces (Zeitpunkte als `Date`, so wie sie aus der DB kommen); die SPA
 * liest dieselben Typen ueber `Json<T>` (Zeitpunkte als ISO-String).
 *
 * Wer hier ein Feld aendert, aendert die SQL-Abfrage im Kern/den Integrationen/
 * den Belegen mit; der Typecheck beider Seiten faellt sonst um.
 */
import type {
  Article, AuditLog, Bin, DocumentKopf, Issuer, OrderDetail, Packaging, Parcel, Return, Shipment, StockLevel, User,
} from './entitaeten.js';
import type { Adresse } from './adresse.js';
import type { IntegrationStatus } from './api.js';
import type { KanalModus } from './kanal.js';
import type {
  AuftragStatus, BewegungsGrund, KlaerfallGrund, KorrekturGrund, ProduktionsStatus, RetoureZustand, Route, SendungStatus, Zahlungsstatus,
} from './status.js';
import type { OutboxZiel } from './outbox.js';

/** JSON-Form eines Typs: `Date` wird zum ISO-String (auch verschachtelt). */
export type Json<T> = T extends Date
  ? string
  : T extends ReadonlyArray<infer U>
    ? Json<U>[]
    : T extends object
      ? { [K in keyof T]: Json<T[K]> }
      : T;

/** Seitenweise Liste. Protokoll-Listen der Integrationen liefern `total` statt `gesamt`. */
export interface Seite<T> {
  eintraege: T[];
  gesamt: number;
  seite?: number;
  pro_seite?: number;
}

// ── Auftraege ─────────────────────────────────────────────────────────────

export interface SendungKurz {
  id: string;
  nummer: number;
  route: Route;
  status: SendungStatus;
  klaerfall_grund: KlaerfallGrund | null;
  tracking_nummer: string | null;
}

/** Zeile von GET /auftraege. `status` ist abgeleitet (R8). */
export interface AuftragZeile {
  id: string;
  bestellnummer: string;
  bestellt_at: Date;
  bezahlt_at: Date | null;
  kanal_status: string;
  zahlungsstatus: Zahlungsstatus;
  status: AuftragStatus;
  summe_brutto_cents: number;
  lieferland: string;
  zahlart: string | null;
  zahlart_titel: string | null;
  ist_test: boolean;
  kunde_email: string | null;
  kunde_name: string | null;
  /** Anzahl Positionen. */
  positionen: number;
  routen: Route[];
  sendung_status: SendungStatus[];
  sendungen: SendungKurz[];
}

/** GET /auftraege/:id/timeline: Audit, Sync, Mail und Outbox zum Auftrag, neueste zuerst. */
export interface TimelineEintrag {
  at: Date;
  quelle: 'audit' | 'sync' | 'mail' | 'outbox';
  titel: string;
  text: string | null;
  benutzer: string | null;
}

/** POST /auftraege/:id/adresse */
export interface AdressKorrekturErgebnis {
  detail: OrderDetail;
  hinweise: string[];
}

// ── Packstation ───────────────────────────────────────────────────────────

export interface PackPosition {
  shipment_item_id: string;
  order_item_id: string;
  article_id: string | null;
  position: number;
  route: Route;
  sku: string | null;
  name: string;
  variante: string | null;
  ean: string | null;
  bild_url: string | null;
  bin_code: string | null;
  menge: number;
  gepackt_menge: number;
  override_grund: string | null;
  /** Wie bestaetigt (R36): scan · fach · tippen · enter · laufzettel · override; null = offen. */
  bestaetigt_per?: 'scan' | 'fach' | 'tippen' | 'enter' | 'laufzettel' | 'override' | null;
  /** Freundebuch personalisiert: Laufzettel-Scan Pflicht (Datenschutz). */
  laufzettel_pflicht: boolean;
  /** Naechster offene Laufzettel der Position (bei Menge 1 der einzige). */
  laufzettel: string | null;
  /** N6/R73: alle Laufzettel der Position (je Stueck einer) und die schon gescannten. */
  laufzettel_codes?: string[];
  laufzettel_gescannt?: string[];
  gewicht_g: number | null;
}

export type PackSchritt = 'scannen' | 'verpackung' | 'gewicht' | 'label' | 'label_scan' | 'fertig' | 'klaerfall' | 'status';

/** Stand des automatischen Label-Drucks (R40), aus der Outbox. */
export interface PackDruck {
  /** T4/R86: `fehler`/`aus` = PrintNode gesperrt, es wird nichts gedruckt (PDF im Browser). */
  modus: KanalModus;
  status: 'offen' | 'erledigt' | 'fehlgeschlagen' | 'aus';
  fehler: string | null;
  /** Mock: abgelegte Datei (relativ zum storeos-Ordner). */
  datei: string | null;
  versuche: number;
}

export type PackPaket = Pick<Parcel, 'id' | 'tracking_nummer' | 'tracking_url' | 'carrier' | 'ist_mock' | 'label_format' | 'hat_label_pdf' | 'hat_label_zpl'>;

/** Arbeitsstand einer Sendung an der Packstation (Antwort aller /packstation-Aufrufe). */
export interface PackZustand {
  sendung: Shipment;
  auftrag: {
    id: string;
    bestellnummer: string;
    lieferland: string;
    zahlungsstatus: string;
    /** `<Bestellnummer>-<Sendungsnummer>` */
    kennung: string;
    bezahlt_at: Date | null;
    kundenhinweis: string | null;
  };
  /** Ziel der Sendung (Lieferadresse, sonst Rechnungsadresse). */
  lieferadresse: Adresse | null;
  positionen: PackPosition[];
  fortschritt: { gepackt: number; gesamt: number; vollstaendig: boolean };
  schritt: PackSchritt;
  storno_alarm: boolean;
  verpackung: Packaging | null;
  verpackung_vorschlag: Packaging | null;
  verpackungen: Packaging[];
  /** S1: Kleinpaket freigeschaltet? Steuert die Produktanzeige beim Tippen (gleiche Regel wie das Label). */
  kleinpaket_erlaubt: boolean;
  gewicht_berechnet_g: number;
  /** Rechenwert fuer das Gewichtsfeld (Σ Artikel + gewaehlte Verpackung bzw. Vorschlag), R41. */
  gewicht_vorschlag_g: number;
  gewicht_unvollstaendig: boolean;
  /** R104: mindestens ein Artikel ohne bekanntes Gewicht. Der Rechenwert ist dann nur ein Teilwert, Wiegen ist Pflicht. */
  gewicht_unbekannt: boolean;
  /** Carrier (R34): primaer, Fallback (nur bei technischem Fehler), gewaehlt = der Carrier des aktuellen Labels. */
  carrier: { primaer: string; fallback: string | null; gewaehlt: string | null };
  /** Labelformat dieser Station (R41: pdf_a5). */
  label_format: string;
  /** Automatischer Label-Druck ueber PrintNode/Mock (R40). null = noch kein Label. */
  druck: PackDruck | null;
  beilagen: string[];
  paket: PackPaket | null;
  label_bestaetigt: boolean;
  /** Sekunden seit dem letzten bestaetigten Label an diesem Arbeitsplatz (Kennzahl). */
  sek_seit_letztem_label: number | null;
}

/** POST /packstation/:id/scan */
export type PackScanErgebnis = PackZustand & { ergebnis: 'ok' | 'abgeschlossen'; gescannt: string };

// ── Tagesabschluss ────────────────────────────────────────────────────────

export interface TagesabschlussSendung {
  sendung_id: string;
  order_id: string;
  bestellnummer: string;
  kennung: string;
  kunde_name: string | null;
  lieferland: string;
  tracking_nummer: string | null;
  carrier: string | null;
  versandprodukt: string | null;
  gewicht_g: number | null;
  label_bestaetigt: boolean;
  label_at: Date | null;
}

/** GET /tagesabschluss */
export interface TagesabschlussSicht {
  /** Cut-off fuer Versand am selben Tag (Einstellungen, Default 14:00). */
  cutoff?: string;
  /** Uhrzeit der taeglichen DHL-Abholung; null = noch offen (Jour fixe, R45). */
  abholung?: string | null;
  datum: string;
  anzahl: number;
  nicht_bestaetigt: number;
  je_produkt: Record<string, number>;
  sendungen: TagesabschlussSendung[];
  verlauf: Array<{ id: string; datum: string; anzahl: number; modus: string; erwartet_anzahl: number | null; hinweis: string | null; benutzer: string | null; created_at: Date }>;
  /** B5: stornierte Sendungen, deren Ware noch nicht zurueck im Fach ist (Paket evtl. schon am Abholplatz). */
  storno_alarme: StornoAlarm[];
}

/** B5/R54: Storno-Alarm (Klaerfall mit Text „STORNO…“), bis die Packkraft die Ware zurueckgelegt hat. */
export interface StornoAlarm {
  sendung_id: string;
  order_id: string;
  kennung: string;
  text: string | null;
  /** Status, in dem der Storno die Sendung traf (in_pick/gepackt/label_erstellt). */
  status_vor: string | null;
  /** Label beim Carrier storniert? null = es gab kein Label. */
  label_storniert: boolean | null;
  seit: Date;
}

/** POST /tagesabschluss `{ erwartet?, force?, sendung_ids? }` */
export interface TagesabschlussErgebnis {
  anzahl: number;
  abschluss_ids: string[];
  sendung_ids: string[];
  hinweise: string[];
  /** W14: Sendungen, die sich zwischen Liste und Abschluss geaendert haben (z. B. Storno), werden uebersprungen. */
  uebersprungen: Array<{ sendung_id: string; kennung: string; grund: string }>;
}

// ── Dashboard ─────────────────────────────────────────────────────────────

export interface DashboardDaten {
  zaehler_sendungen: Record<SendungStatus, number>;
  zaehler_auftraege: Record<AuftragStatus, number>;
  klaerfaelle: Array<{ sendung_id: string; order_id: string; bestellnummer: string; grund: KlaerfallGrund | null; text: string | null; seit: Date; storno_alarm: boolean }>;
  nachbestellen: Array<{ article_id: string; sku: string; name: string; variante: string | null; physisch: number; verfuegbar: number; meldebestand: number }>;
  verspaetet: Array<{ sendung_id: string; order_id: string; bestellnummer: string; status: SendungStatus; bezahlt_at: Date; werktage: number }>;
  retourenfristen: Array<{ return_id: string; nummer: string; order_id: string; bestellnummer: string; status: Return['status']; frist_at: Date; tage_bis_frist: number }>;
  outbox: { fehlgeschlagen: number; offen: number; trockenlauf: number; fehler: Array<{ id: string; typ: string; versuche: number; letzter_fehler: string | null }> };
  /** Median Label bis Label-Scan. */
  packzeit: { n: number; median_sek: number | null };
  heute: { label_erstellt: number; uebergeben: number; importiert: number };
  /** B5: Storno nach der Uebergabe (Paket ist unterwegs) – Retoure bzw. Annahmeverweigerung erwarten. */
  storno_nach_versand: Array<{ order_id: string; bestellnummer: string; kennung: string; status: SendungStatus; seit: Date }>;
  /** W5: Waehrend einer laufenden Inventur sieht das Lager keine Bestaende (Blindzaehlung). */
  bestand_verborgen?: boolean;
  stand: string;
}

// ── Produktion ────────────────────────────────────────────────────────────

export interface ProduktionSicht {
  intern: Array<{
    order_item_id: string; order_id: string; sendung_id: string | null; sendung_status: SendungStatus | null; bestellnummer: string; name: string; variante: string | null;
    produktions_status: ProduktionsStatus; laufzettel: string; bezahlt_at: Date; werktage: number; ueberfaellig: boolean;
    /** N6/R73: eine Zeile je Stueck; `stueck` null bei Menge 1, `produktions_status` ist dann der Stand dieses Stuecks. */
    stueck?: number | null; menge?: number;
  }>;
  partner: Array<{
    sendung_id: string; order_id: string; bestellnummer: string; kennung: string; status: SendungStatus; partner_name: string | null;
    /** Positionen als „2× Name · Variante“. */
    positionen: string[];
    bezahlt_at: Date; werktage: number; tracking: string | null;
  }>;
}

/** POST /produktion/scan */
export interface ProduktionScanErgebnis {
  order_item_id: string;
  produktions_status: ProduktionsStatus;
  sendung_id: string | null;
  sendung_status: SendungStatus | null;
  bestellnummer: string;
  meldung: string;
  /** N6/R73: gescanntes Stueck (1..menge) und dessen Stand; `produktions_status` ist der Stand der Position. */
  stueck?: number;
  stueck_status?: ProduktionsStatus;
  menge?: number;
}

/** POST /sendungen/:id/partner-tracking */
export interface PartnerTrackingErgebnis {
  sendung_id: string;
}

// ── Artikel / Lager ───────────────────────────────────────────────────────

/**
 * Bestandszahlen einer Artikelsicht. W5/R59: Waehrend eine Inventur laeuft (offen/eingereicht), sind sie fuer die
 * Rolle lager `null` und `bestand_verborgen` ist true – sonst liesse sich die Blindzaehlung ueber das Artikeldetail umgehen.
 */
export interface BestandZahlen {
  article_id: string;
  physisch: number | null;
  reserviert: number | null;
  verfuegbar: number | null;
  bestand_verborgen?: boolean;
}

export type ArtikelZeile = Article & BestandZahlen & { bin_code: string | null; komponente_sku: string | null; listings: number };

export interface JournalZeile {
  id: string;
  created_at: Date;
  menge: number;
  grund: BewegungsGrund;
  korrektur_grund: KorrekturGrund | null;
  ref_typ: string | null;
  ref_id: string | null;
  bemerkung: string | null;
  bin_code: string | null;
  benutzer: string | null;
}

/** GET /artikel/:id: Artikel mit Bestand, Plaetzen, Journal, aktiven Reservierungen und Kanal-Listings. */
export type ArtikelDetail = Article & BestandZahlen & {
  bin_code: string | null;
  plaetze: Array<{ bin_id: string; code: string; menge: number | null }>;
  journal: JournalZeile[];
  reservierungen: Array<{ id: string; menge: number; created_at: Date; order_id: string; bestellnummer: string }>;
  listings: Array<{ id: string; store_id: string; external_product_id: string; external_variation_id: string | null; bestand_sync: boolean; letzter_push_wert: number | null; letzter_push_at: Date | null; kanal_bestand: number | null }>;
  /** Komponente der einstufigen Stueckliste (R110), sonst null. */
  komponente?: { id: string; sku: string; name: string; variante: string | null } | null;
  /** Artikel, die diesen als Komponente nutzen (R110). */
  verwendet_in?: Array<{ id: string; sku: string; name: string; komponente_menge: number | null }>;
};

/** Praefix interner Artikel (nur StoreOS, kein Kanal-Listing, R109). */
export const INTERN_SKU_PRAEFIX = 'NG-INT-';

/**
 * SKU-Vorschlag fuer einen internen Artikel (R109): NG-INT- + Woerter des Namens in Grossbuchstaben, Umlaute
 * umschrieben, Sonderzeichen als Trenner, hoechstens 24 Zeichen nach dem Praefix (an der Wortgrenze gekuerzt).
 */
export function skuVorschlagIntern(name: string): string {
  const woerter = name.toUpperCase()
    .replace(/Ä/g, 'AE').replace(/Ö/g, 'OE').replace(/Ü/g, 'UE').replace(/ß/g, 'SS')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .split(/[^A-Z0-9]+/).filter(Boolean);
  let rest = '';
  for (const w of woerter) {
    const k = rest ? `${rest}-${w}` : w;
    if (k.length > 24) { if (!rest) rest = w.slice(0, 24); break; }
    rest = k;
  }
  return `${INTERN_SKU_PRAEFIX}${rest}`;
}

export type Lagerplatz = Omit<Bin, 'created_at'>;

export interface ArtikelTreffer {
  id: string; sku: string; name: string; variante: string | null; ean: string | null; standard_bin_id: string | null; bestand_fuehren: boolean;
}

/** POST /wareneingang `{ positionen: [{ article_id | code, menge, bin_id? }], lieferant?, referenz? }` */
export interface WareneingangErgebnis {
  lieferung_id: string;
  gebucht: Array<{ article_id: string; sku: string; menge: number; bin_id: string | null; bin_code: string | null; verfuegbar: number | null }>;
  /** Sendungen, die durch den Zugang von wartet_bestand auf bereit gingen. */
  bedient: Array<{ sendung_id: string; bestellnummer: string }>;
}

/** GET /protokoll/bewegungen */
export type BewegungZeile = JournalZeile & { article_id: string; sku: string; name: string; variante: string | null };

// ── Retouren ──────────────────────────────────────────────────────────────

export interface RetourePosition {
  id: string;
  order_item_id: string;
  menge: number;
  zustand: RetoureZustand | null;
  bin_id: string | null;
  bemerkung: string | null;
  name: string;
  variante: string | null;
  sku: string | null;
  /** Personalisiert: vom Widerruf ausgeschlossen. */
  personalisiert: boolean;
  einzel_brutto_cents: number;
}

export type RetoureZeile = Return & {
  /** R37: Retourenlabel nur als Ausnahme der Leitung (Kunde traegt laut AGB die Ruecksendekosten). */
  hat_retourenlabel?: boolean;
  retourenlabel_tracking?: string | null;
  retourenlabel_grund?: string | null;
  retourenlabel_at?: Date | null;
  bestellnummer: string;
  kunde_email: string | null;
  kunde_name: string | null;
  positionen: RetourePosition[];
  /** N3/R74: Vorschlag fuer die Erstattung = bezahlter Positionsbetrag nach Rabatt, anteilig je Stueck (Server rechnet). */
  erstattung_vorschlag_cents?: number;
};

// ── Protokoll ─────────────────────────────────────────────────────────────

export type AuditZeile = AuditLog & { benutzer: string };

// ── Belege ────────────────────────────────────────────────────────────────

/** Zeile von GET /belege (Liste: `{ eintraege, gesamt }`). */
export type BelegZeile = DocumentKopf & {
  bestellnummer: string | null;
  bezug_nummer: string | null;
  /** Gutschrift: kanal (Woo-Erstattung), retoure (Erstattung zu einer Retoure), manuell. */
  quelle: 'kanal' | 'retoure' | 'manuell' | null;
  /** Woo-Refund-ID bzw. Retourennummer (RT-…). */
  referenz: string | null;
  export_status: 'offen' | 'uebertragen' | 'fehler' | null;
};

/** GET /belege/:id/export-status */
export interface BelegExportStatus {
  exporte: Array<{ id: string; adapter: string; modus: 'mock' | 'live'; status: 'offen' | 'uebertragen' | 'fehler'; request: unknown; response: unknown; created_at: Date }>;
  outbox: Array<{ id: string; typ: string; status: string; versuche: number; letzter_fehler: string | null; naechster_versuch_at: Date | null; erledigt_at: Date | null }>;
}

// ── Integrationen, Einstellungen ──────────────────────────────────────────

/** GET /integrationen/status */
export interface IntegrationenStatus {
  integrationen: IntegrationStatus[];
  wooSchreiben: { status: boolean; notiz: boolean; bestand: boolean };
  carrierDefault: 'dhl' | 'shipcloud' | 'mock';
  /** BuchhaltungsButler: Debitorenkonto aus BHB_DEBITOR_KONTO (null = nicht senden). */
  buchhaltung: { debitorKonto: number | null };
  /** R34: primaerer Carrier und Fallback (CARRIER_PRIMAER/CARRIER_FALLBACK), Labelformat (R41), Versandlaender (R39). */
  versand?: { primaer: string; fallback: string | null; labelFormat: string; versandLaender: string[] };
  /** R40: Druckziele je Dokumentart (PRINTNODE_DRUCKER_x, PRINTNODE_FACH_x, PRINTNODE_PAPIER_x), Modus des Druckdienstes. */
  druck?: { modus: KanalModus; labelAutomatisch: boolean; ziele: Record<string, { druckerId: number | null; fach: string | null; papier: string | null }> };
  /** R42: Partner-Tracking-Webhook konfiguriert (Token gesetzt)? */
  partnerWebhook?: { modus: 'mock' | 'live'; pfad: string };
  stores: Array<{ id: string; code: string; name: string; typ: string; sync_cursor: Date | null; letzter_sync_at: Date | null; aktiv: boolean; adapter: KanalModus }>;
  jobs: string[];
  letzteLaeufe: Array<{ art: string; ergebnis: string; meldung: string; created_at: Date }>;
  outbox: Array<{ ziel: OutboxZiel; status: string; anzahl: number }>;
  outboxNaechsterVersuch: Date | null;
}

/** Ergebnis eines Produktabgleichs je Store (R100–R103), POST /integrationen/produkte/abgleich und Job woo.produkte. */
export interface ProduktAbgleichStore {
  store_code: string;
  /** Verkaufbare Einheiten, die der Kanal geliefert hat (simple + Variationen). */
  produkte: number;
  artikel_neu: number;
  artikel_aktualisiert: number;
  neue_listings: number;
  /** Ohne SKU nicht angelegt (Warnung im Sync-Log je Produkt). */
  ohne_sku: number;
  /** Nicht veroeffentlichte Variationen/Produkte, nicht angelegt. */
  inaktiv: number;
  gewicht_unbekannt: number;
  bestand_gefuehrt: number;
  /** Summe der als `startbestand_kanal` gebuchten Stueck (nur vor der Umschaltung). */
  startbestand_summe: number;
  negativ_auf_null: number;
  /** Nach der Umschaltung: neu angelegt mit Bestand 0 (Klaerhinweis im Sync-Log). */
  nach_umschaltung_null: number;
  fremdaenderungen: number;
  nach_route: Record<string, number>;
  nach_route_quelle: Record<string, number>;
}

export interface ProduktAbgleichErgebnis {
  stores: ProduktAbgleichStore[];
  /** Klaerfaelle „Artikel unbekannt“ nach dem Abgleich neu geprueft (R103). */
  klaerfaelle: { geprueft: number; geloest: number; offen: number; positionen_zugeordnet: number };
  dauer_ms: number;
}

export const BEILAGEN_BEDINGUNGEN = ['immer', 'geschenk', 'ausland'] as const;
export type BeilagenBedingung = (typeof BEILAGEN_BEDINGUNGEN)[number];

export interface Beilagenregel {
  id: string;
  name: string;
  /** immer · geschenk (Lieferadresse ≠ Rechnungsadresse) · ausland (Lieferland ≠ DE) */
  bedingung: BeilagenBedingung;
  beilage: string;
  aktiv: boolean;
}

/** GET /einstellungen (Leitung lesend, PUT Admin). Integrationen kommen aus .env (GET /integrationen/status), Aussteller aus GET /aussteller. */
export interface Einstellungen {
  marke: { id: string; code: string; name: string; absender_name: string; absender_email: string };
  verpackungen: Packaging[];
  beilagenregeln: Beilagenregel[];
  /** Cut-off fuer Same-Day-Versand, HH:MM. */
  cutoff: string;
  /** Automatischer Tagesabschluss, HH:MM (lokale Serverzeit). */
  tagesabschluss_auto: string;
  benutzer: User[];
}

/** PUT /einstellungen (admin) */
export interface EinstellungenAenderung {
  cutoff?: string;
  tagesabschluss_auto?: string;
  beilagenregeln?: Beilagenregel[];
}

/** Aussteller mit Belegzahl (GET /aussteller). */
export type AusstellerZeile = Issuer & { belege: number; letzter_beleg: string | null };

/**
 * PUT /aussteller/:id (admin). Gilt nur fuer NEUE Belege: festgeschriebene Belege tragen
 * ihre Ausstellerdaten eingefroren in `documents.daten` und im PDF (GoBD, R4).
 */
export type AusstellerAenderung = Partial<Pick<Issuer, 'name' | 'adresse' | 'email' | 'telefon' | 'ust_id' | 'steuernummer' | 'register' | 'geschaeftsfuehrung' | 'bank' | 'kleinunternehmer' | 'gueltig_ab'>>;

// ── Inventur (R38) ────────────────────────────────────────────────────────

/**
 * `umschaltung`/`stichtag`: Blindzaehlung je Fach (R38). `gefuehrt_erst`: geführte Erstinventur Artikel für Artikel am
 * Tablet über mehrere Tage (R126).
 */
export const INVENTUR_ARTEN = ['umschaltung', 'stichtag', 'gefuehrt_erst'] as const;
export type InventurArt = (typeof INVENTUR_ARTEN)[number];
/**
 * Erstzaehlungen ersetzen den Startbestand aus dem Kanal (R101) und gelten nach der Freigabe als Erstzaehlung der
 * Umschaltung (Runbook 4.15 wird dann zur Stichprobe, R126).
 */
export const ERSTZAEHLUNG_ARTEN: readonly InventurArt[] = ['umschaltung', 'gefuehrt_erst'];
export type InventurStatus = 'offen' | 'eingereicht' | 'freigegeben' | 'verworfen';

/** Zeile von GET /inventuren. */
export interface InventurKopf {
  id: string;
  art: InventurArt;
  titel: string;
  status: InventurStatus;
  created_at: Date;
  eingereicht_at: Date | null;
  freigegeben_at: Date | null;
  freigegeben_von: string | null;
  zaehlungen: number;
  /** Nur nach Freigabe: Summe der gebuchten Differenzen (Stueck, mit Vorzeichen) und Zahl der Positionen mit Differenz. */
  differenzen: number | null;
  differenz_summe: number | null;
}

/**
 * Eine Zaehlung (Artikel an Fach). Blindzaehlung: `soll` und `differenz` sind null, solange die
 * Inventur offen ist oder die Rolle unter Leitung liegt (`soll_sichtbar` in InventurSicht).
 */
export interface InventurZaehlung {
  id: string;
  article_id: string;
  sku: string;
  name: string;
  variante: string | null;
  /** Bei `gefuehrt_erst` optional (Artikel ohne Platz). */
  bin_id: string | null;
  bin_code: string | null;
  gezaehlt: number;
  gezaehlt_von: string | null;
  gezaehlt_at: Date;
  soll: number | null;
  differenz: number | null;
  // ── nur `gefuehrt_erst` (R126); Werte ausser `ausgelaufen`/`nachzaehlen` nur fuer die Leitung nach dem Einreichen ──
  /** „Produkt ausgelaufen“ gemeldet; `gezaehlt` ist dann die Restmenge. */
  ausgelaufen?: boolean;
  /** Von der Leitung zum Nachzählen zurückgegeben. */
  nachzaehlen?: boolean;
  /** Menge aus Bestellungen nach dem Zählzeitpunkt (bis zur Umschaltung, inkl. Komponenten). */
  verkauft_seit?: number | null;
  /** Bestand nach der Freigabe = gezählt − verkauft seit Zählung + Journalbewegungen seit Zählung, nie unter 0. */
  ergebnis?: number | null;
  /** Bisheriger Wert im Journal (Startbestand aus Woo bzw. Bestand). */
  bisher?: number | null;
  /** Ohne Bestandsführung (Woo manage_stock aus); gebucht wird trotzdem. */
  bestand_fuehren?: boolean;
  /** Hinweis an die Leitung, z. B. „Mehr verkauft als gezählt, auf 0 gesetzt“. */
  warnung?: string | null;
  kategorie?: string | null;
}

/** GET /inventuren/:id */
export interface InventurSicht {
  kopf: InventurKopf;
  zaehlungen: InventurZaehlung[];
  soll_sichtbar: boolean;
}

/** Ein Artikel der geführten Erstinventur, wie das Tablet ihn zeigt. Kein Bestand, kein Preis (Blindzählung, R59). */
export interface InventurArtikelKarte {
  article_id: string;
  sku: string;
  name: string;
  variante: string | null;
  kategorie: string | null;
  bild_url: string | null;
  /** Standardplatz des Artikels (Vorschlag), null = keiner. */
  bin_id: string | null;
  bin_code: string | null;
  /** Interner Artikel (NG-INT-…, R109). */
  intern: boolean;
  /** Von der Leitung zum Nachzählen zurückgegeben. */
  nachzaehlen: boolean;
  /** Nur beim Korrigieren (`?article_id=`): was die Kollegin selbst eingetragen hat. Nie ein Soll. */
  eingabe: { aktion: 'gezaehlt' | 'ausgelaufen' | 'spaeter'; gezaehlt: number | null; bin_code: string | null; at: Date } | null;
}

/** GET /inventuren/:id/weiter — Stand der geführten Erstinventur fürs Tablet. */
export interface InventurWeiter {
  inventur: { id: string; titel: string; status: InventurStatus; art: InventurArt };
  /** Nächster Artikel (bzw. der angefragte); null = Warteschlange leer. */
  aktuell: InventurArtikelKarte | null;
  /** Vorschau „Nächster: …“ */
  naechster: { article_id: string; name: string; variante: string | null } | null;
  /** Letzte Eingabe (für „Zurück“). */
  letzte: { article_id: string; name: string; variante: string | null; aktion: 'gezaehlt' | 'ausgelaufen' | 'spaeter'; at: Date } | null;
  fortschritt: {
    gezaehlt: number;
    gesamt: number;
    heute: number;
    spaeter: number;
    nachzaehlen: number;
    /** Anteil am Warenwert (Preis × bisheriger Bestand) der gezählten Artikel, 0..1. Nur Leitung, sonst null. */
    warenwert_anteil: number | null;
  };
  spaeter: Array<{ article_id: string; sku: string; name: string; variante: string | null }>;
}

/** POST /inventuren/:id/weiter */
export interface InventurWeiterEingabe {
  article_id: string;
  aktion: 'zaehlen' | 'ausgelaufen' | 'spaeter';
  /** zaehlen: Menge ≥ 0 (Pflicht). ausgelaufen: Restmenge (optional, sonst 0). */
  gezaehlt?: number;
  /** Lagerplatz: vorhandener per ID oder Code; mit `platz_anlegen` wird ein neuer Code angelegt. */
  bin_id?: string;
  bin_code?: string;
  platz_anlegen?: boolean;
}

/** GET /artikel/ausgelaufen — Liste für Thomas (keine Änderung in Woo, R126). */
export interface AusgelaufenZeile {
  article_id: string;
  sku: string;
  name: string;
  variante: string | null;
  woo_id: string | null;
  restmenge: number;
  /** Aktueller Bestand (physisch); nur Leitung. */
  physisch: number;
  ausgelaufen_at: Date;
  ausgelaufen_von: string | null;
}

/** GET /inventuren/:id/fach?code= — Zaehlliste eines Fachs, OHNE Sollbestand (Blindzaehlung). */
export interface InventurFach {
  bin_id: string;
  bin_code: string;
  artikel: Array<{ article_id: string; sku: string; name: string; variante: string | null; ean: string | null; gezaehlt: number | null }>;
}
